import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionLangBotexamlangidTranslationQueryKey,
  usePutApiV1ExamRevisionQuestion,
} from '@/shared/api/generated';
import type { BotExamLangTranslationQuestionViewModel } from '@/shared/api/generated/types/BotExamLangTranslationQuestionViewModel';

/**
 * Persiste a tradução de uma questão via `PUT /api/v1/exam/revision/question`
 * (`BotExamLangTranslationQuestionViewModel`), invalidando a tradução do idioma ao
 * concluir. O payload é montado pelo consumidor (a partir do estado editado + DTO
 * original), pois o modelo de exibição não carrega todos os campos do contrato.
 *
 * @param botExamLangId - ID do idioma da prova cuja tradução está sendo editada
 * @returns Handler de persistência e estado de loading
 *
 * @example
 * ```tsx
 * const { saveQuestion, isSaving } = useSaveRevisionTranslationQuestion(botExamLangId);
 * saveQuestion(payload);
 * ```
 */
export function useSaveRevisionTranslationQuestion(botExamLangId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePutApiV1ExamRevisionQuestion({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('translation.toast.saveSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionLangBotexamlangidTranslationQueryKey(botExamLangId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('translation.toast.saveError') }),
    },
  });

  const saveQuestion = (
    data: BotExamLangTranslationQuestionViewModel,
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutation.mutate(
      { data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { saveQuestion, isSaving: mutation.isPending } as const;
}
